/**
 * +rogue-specs: shadow clones you can see (docs/design/classes/specs.md "Rogue"). The sim keeps
 * Silhouettes / Phantoms as aura stacks; this draws one translucent figure per stack:
 *
 *  - Shadowdancer Silhouettes stay where the hero stood when they appeared (Umbral Step leaves one
 *    behind as it blinks), flicker in the spec's violet, lunge at the target when their echo of
 *    Umbral Finale lands, and flare when they fan with Shadow Fan.
 *  - Mirage Phantoms orbit the hero in pale cyan and shatter into shards when a dodge breaks one.
 *
 * Stack counts come from the aura events (apply / stack / remove), so the module needs no sim
 * access. A few figures at most: per-ghost materials, shared geometry, no allocations per frame.
 */
import * as THREE from 'three';
import type { SimEvent, UnitId } from '@keystone/sim';
import { MIRAGE_AURA, SHADOWDANCER, SHADOWDANCER_AURA } from '@keystone/content';
import type { RenderUnit } from '../../types';
import { groundY } from '../ground';
import type { RGB } from './table';

type Kind = 'silhouette' | 'phantom';

interface Ghost {
  unitId: UnitId;
  kind: Kind;
  root: THREE.Group;
  mat: THREE.MeshBasicMaterial;
  /** Fixed spot (silhouettes) or orbit slot (phantoms). */
  x: number;
  y: number;
  facing: number;
  slot: number;
  born: number;
  /** 'idle' → 'spent' (waiting for its echo) → 'strike' / 'fade'. */
  state: 'idle' | 'spent' | 'strike' | 'fade';
  t0: number;
  /** Strike: where it lunges. */
  tx: number;
  ty: number;
  flare: number;
}

/** Particle hook (ToonFx.burst under the hood): kind, sim-plane position, height, colours. */
export type CloneBurst = (kind: 'smoke' | 'shards' | 'ring' | 'slash', x: number, y: number, h: number, color: RGB, accent: RGB) => void;

/** `body` is drawn with normal blending (a dark shape reads as a shadow on bright grass), the rim additively. */
const LOOK: Record<Kind, { color: RGB; accent: RGB; body: RGB; opacity: number; rim: number }> = {
  silhouette: { color: [0.36, 0.24, 0.72], accent: [0.78, 0.68, 1.0], body: [0.13, 0.08, 0.27], opacity: 0.62, rim: 0.5 },
  phantom: { color: [0.45, 0.8, 0.92], accent: [0.85, 0.97, 1.0], body: [0.62, 0.86, 0.95], opacity: 0.3, rim: 0.35 },
};
const AURA_KIND: Readonly<Record<string, Kind>> = { [SHADOWDANCER_AURA.silhouettes]: 'silhouette', [MIRAGE_AURA.phantoms]: 'phantom' };
const MAX_PER_UNIT = 6;
const SPENT_WAIT = 1.2;
const STRIKE_S = 0.14;
const FADE_S = 0.35;

/** A hooded figure with two daggers, ≈ 1.8 yd tall, facing +Z (rotated per ghost). */
function figureGeometries(): THREE.BufferGeometry[] {
  const cloak = new THREE.LatheGeometry(
    [new THREE.Vector2(0.0, 0.0), new THREE.Vector2(0.42, 0.02), new THREE.Vector2(0.34, 0.5), new THREE.Vector2(0.26, 1.05), new THREE.Vector2(0.3, 1.32), new THREE.Vector2(0.12, 1.4), new THREE.Vector2(0.0, 1.42)],
    14,
  );
  const head = new THREE.SphereGeometry(0.2, 12, 8).translate(0, 1.56, 0.02);
  const hood = new THREE.ConeGeometry(0.26, 0.42, 12, 1, true).translate(0, 1.66, -0.03);
  const bladeR = new THREE.BoxGeometry(0.05, 0.62, 0.02).rotateX(-0.9).translate(0.42, 0.95, 0.28);
  const bladeL = new THREE.BoxGeometry(0.05, 0.62, 0.02).rotateX(-0.6).translate(-0.42, 0.9, 0.24);
  return [cloak, head, hood, bladeR, bladeL];
}

export class CloneFx {
  readonly group = new THREE.Group();
  private readonly geos = figureGeometries();
  private ghosts: Ghost[] = [];
  private readonly counts = new Map<string, number>();
  private now = 0;
  private slotSeq = 0;

  constructor(private readonly burst: CloneBurst) {
    this.group.name = 'fx:clones';
  }

  /** Feed every sim event of the frame (cheap: two aura ids and two echo ids). */
  onEvent(e: SimEvent, units: ReadonlyMap<UnitId, RenderUnit>): void {
    switch (e.type) {
      case 'applybuff':
      case 'applybuffstack':
      case 'refreshbuff':
      case 'removebuffstack': {
        const kind = AURA_KIND[e.abilityGameID];
        if (!kind || !e.targetID) return;
        const n = e.type === 'refreshbuff' ? undefined : (e.stack ?? 1);
        if (n !== undefined) this.setCount(e.targetID, e.abilityGameID, kind, n, units);
        return;
      }
      case 'removebuff': {
        const kind = AURA_KIND[e.abilityGameID];
        if (kind && e.targetID) this.setCount(e.targetID, e.abilityGameID, kind, 0, units);
        return;
      }
      case 'damage': {
        if (e.abilityGameID === SHADOWDANCER.silhouetteEcho) this.strike(e.sourceID, e.targetID, units);
        else if (e.abilityGameID === SHADOWDANCER.silhouetteFan) this.fan(e.sourceID);
        return;
      }
      default:
        return;
    }
  }

  private setCount(unitId: UnitId, auraId: string, kind: Kind, n: number, units: ReadonlyMap<UnitId, RenderUnit>): void {
    const key = `${unitId}|${auraId}`;
    const before = this.counts.get(key) ?? 0;
    const want = Math.min(MAX_PER_UNIT, Math.max(0, n));
    if (want === 0) this.counts.delete(key);
    else this.counts.set(key, want);
    const u = units.get(unitId);
    if (want > before && u) for (let i = before; i < want; i++) this.spawn(u, kind);
    else if (want < before) {
      const live = this.ghosts.filter((g) => g.unitId === unitId && g.kind === kind && g.state === 'idle');
      for (let i = 0; i < before - want && i < live.length; i++) this.spend(live[i]!);
    }
  }

  private spawn(u: RenderUnit, kind: Kind): void {
    const look = LOOK[kind];
    const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(...look.body), transparent: true, opacity: 0, depthWrite: false });
    const root = new THREE.Group();
    for (const g of this.geos) root.add(new THREE.Mesh(g, mat));
    // A faint rim in the accent colour (the edge you notice in the dark).
    const rimMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(...look.color), transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.BackSide });
    const rim = new THREE.Mesh(this.geos[0]!, rimMat);
    rim.scale.setScalar(1.06);
    root.add(rim);
    root.userData.rim = rimMat;
    const slot = this.slotSeq++;
    // Silhouettes stand exactly where the hero stood; Phantoms take an orbit slot around it.
    const g: Ghost = { unitId: u.id, kind, root, mat, x: u.pos.x, y: u.pos.y, facing: u.facing, slot, born: this.now, state: 'idle', t0: this.now, tx: 0, ty: 0, flare: 0 };
    if (kind === 'silhouette') {
      // Step aside a little so a clone made while standing still doesn't hide inside the hero.
      const a = u.facing + Math.PI + ((slot % 3) - 1) * 0.9;
      g.x += Math.cos(a) * 0.9;
      g.y += Math.sin(a) * 0.9;
    }
    this.group.add(root);
    this.ghosts.push(g);
    this.place(g, null);
    this.burst('smoke', g.x, g.y, 0.8, look.color, look.accent);
  }

  private spend(g: Ghost): void {
    if (g.kind === 'phantom') {
      // A dodge broke it: shards, then gone.
      this.burst('shards', g.x, g.y, 1.0, LOOK.phantom.color, LOOK.phantom.accent);
      g.state = 'fade';
      g.t0 = this.now;
      return;
    }
    g.state = 'spent';
    g.t0 = this.now;
  }

  /** An echo landed: the oldest spent Silhouette of that hero lunges at the target. */
  private strike(sourceId: UnitId | undefined, targetId: UnitId | undefined, units: ReadonlyMap<UnitId, RenderUnit>): void {
    const g = this.ghosts.find((x) => x.unitId === sourceId && x.kind === 'silhouette' && x.state === 'spent') ?? this.ghosts.find((x) => x.unitId === sourceId && x.kind === 'silhouette' && x.state === 'idle');
    const t = targetId ? units.get(targetId) : undefined;
    if (!g || !t) return;
    const dx = t.pos.x - g.x;
    const dy = t.pos.y - g.y;
    const d = Math.hypot(dx, dy) || 1;
    const reach = Math.max(0, d - (t.radius + 0.6));
    g.tx = g.x + (dx / d) * reach;
    g.ty = g.y + (dy / d) * reach;
    g.facing = Math.atan2(dy, dx);
    g.state = 'strike';
    g.t0 = this.now;
  }

  /** Shadow Fan from every Silhouette: a flare and a ring at each clone of that hero. */
  private fan(sourceId: UnitId | undefined): void {
    for (const g of this.ghosts) {
      if (g.unitId !== sourceId || g.kind !== 'silhouette' || g.state !== 'idle' || this.now - g.flare < 0.3) continue;
      g.flare = this.now;
      this.burst('ring', g.x, g.y, 0.9, LOOK.silhouette.color, LOOK.silhouette.accent);
    }
  }

  private place(g: Ghost, owner: RenderUnit | null | undefined): void {
    let x = g.x;
    let y = g.y;
    let facing = g.facing;
    if (g.kind === 'phantom' && owner) {
      // Orbit the hero: slots spread around it, drifting slowly.
      const a = g.slot * 2.1 + this.now * 0.6;
      x = owner.pos.x + Math.cos(a) * 1.5;
      y = owner.pos.y + Math.sin(a) * 1.5;
      facing = owner.facing;
      g.x = x;
      g.y = y;
    }
    if (g.state === 'strike') {
      const k = Math.min(1, (this.now - g.t0) / STRIKE_S);
      x = g.x + (g.tx - g.x) * k;
      y = g.y + (g.ty - g.y) * k;
    }
    g.root.position.set(x, groundY(x, y), -y);
    // Model faces +Z; sim facing θ points along (cos θ, −sin θ) in three: yaw = π/2 + θ.
    g.root.rotation.y = Math.PI / 2 + facing;
  }

  update(dt: number, units: ReadonlyMap<UnitId, RenderUnit>): void {
    this.now += dt;
    if (!this.ghosts.length) return;
    let w = 0;
    for (const g of this.ghosts) {
      const owner = units.get(g.unitId);
      const look = LOOK[g.kind];
      const age = this.now - g.born;
      let alpha = look.opacity * Math.min(1, age / 0.18);
      // A gentle flicker (shadows) or shimmer (mirrors).
      alpha *= g.kind === 'silhouette' ? 0.85 + 0.15 * Math.sin(this.now * 9 + g.slot * 1.7) : 0.8 + 0.2 * Math.sin(this.now * 5 + g.slot);
      if (this.now - g.flare < 0.25) alpha = Math.min(0.9, alpha * 1.8);
      let done = !owner || !owner.alive;
      if (g.state === 'spent' && this.now - g.t0 > SPENT_WAIT) {
        g.state = 'fade';
        g.t0 = this.now;
      }
      if (g.state === 'strike') {
        const k = (this.now - g.t0) / STRIKE_S;
        alpha = Math.min(0.95, alpha * 2);
        if (k >= 1) {
          this.burst('slash', g.tx, g.ty, 1.0, look.color, look.accent);
          g.x = g.tx;
          g.y = g.ty;
          g.state = 'fade';
          g.t0 = this.now;
        }
      }
      if (g.state === 'fade') {
        const k = (this.now - g.t0) / FADE_S;
        alpha *= Math.max(0, 1 - k);
        if (k >= 1) done = true;
      }
      if (done) {
        this.group.remove(g.root);
        g.mat.dispose();
        (g.root.userData.rim as THREE.Material | undefined)?.dispose();
        continue;
      }
      g.mat.opacity = alpha;
      (g.root.userData.rim as THREE.MeshBasicMaterial).opacity = (alpha / look.opacity) * look.rim;
      this.place(g, owner);
      this.ghosts[w++] = g;
    }
    this.ghosts.length = w;
  }

  clear(): void {
    for (const g of this.ghosts) {
      this.group.remove(g.root);
      g.mat.dispose();
      (g.root.userData.rim as THREE.Material | undefined)?.dispose();
    }
    this.ghosts = [];
    this.counts.clear();
  }

  dispose(): void {
    this.clear();
    for (const g of this.geos) g.dispose();
  }
}
